'use client';
import {lazy,Suspense,useEffect,useState,type CSSProperties,type ComponentType} from 'react';
import {HOME_TEMPLATES,DEFAULT_HOME_TEMPLATE,usePublicCopy} from '../content/navigation';
import {GlassHome,MotionContext,MotionControlContext} from './templates/GlassHome';
import styles from './home.module.css';
import type {AgentSiteContent,AgentNotification} from '@zzy/contracts';
import {PublishedSiteInfo} from '../../agent/PublishedSiteInfo';

const components:Record<string,ComponentType<{brandName:string;subtitle?:string}>>={
 glass:GlassHome,
 synthora:lazy(()=>import('./templates/SynthoraHome').then(m=>({default:m.SynthoraHome}))),
 orvante:lazy(()=>import('./templates/OrvanteHome').then(m=>({default:m.OrvanteHome}))),
 terrena:lazy(()=>import('./templates/TerrenaHome').then(m=>({default:m.TerrenaHome}))),
 verra:lazy(()=>import('./templates/VerraHome').then(m=>({default:m.VerraHome}))),
 lumora:lazy(()=>import('./templates/LumoraHome').then(m=>({default:m.LumoraHome}))),
 axon:lazy(()=>import('./templates/AxonHome').then(m=>({default:m.AxonHome}))),
 'axon-motion':lazy(()=>import('./templates/AxonMotionHome').then(m=>({default:m.AxonMotionHome}))),
 vexel:lazy(()=>import('./templates/VexelHome').then(m=>({default:m.VexelHome}))),
 digitwist:lazy(()=>import('./templates/DigitwistHome').then(m=>({default:m.DigitwistHome}))),
 neuralyn:lazy(()=>import('./templates/NeuralynHome').then(m=>({default:m.NeuralynHome}))),
 sellix:lazy(()=>import('./templates/SellixHome').then(m=>({default:m.SellixHome}))),
};
const motionKey='zzy-home-motion';

export function HomePage({published,notices=[]}:{published?:ReturnType<typeof AgentSiteContent.parse>;notices?:ReturnType<typeof AgentNotification.parse>[]}){
 const {t}=usePublicCopy();
 const [templateId,setTemplateId]=useState<string>(published?.template??DEFAULT_HOME_TEMPLATE),[preview,setPreview]=useState(false),[cover,setCover]=useState(false);
 const [motion,setMotion]=useState(false),[reduced,setReduced]=useState(false);
 useEffect(()=>{
  const params=new URLSearchParams(location.search),id=params.get('homePreview');
  if(id){setTemplateId(HOME_TEMPLATES.some(item=>item.id===id)?id:DEFAULT_HOME_TEMPLATE);setPreview(true);setCover(params.get('homeCover')==='1'&&HOME_TEMPLATES.some(item=>item.id===id))}
  const media=matchMedia('(prefers-reduced-motion: reduce)');
  const sync=()=>setReduced(media.matches);
  sync();media.addEventListener('change',sync);
  try{setMotion(localStorage.getItem(motionKey)!=='off')}catch{setMotion(true)}
  return()=>media.removeEventListener('change',sync);
 },[]);
 useEffect(()=>{if(!new URLSearchParams(location.search).has('homePreview'))setTemplateId(published?.template??DEFAULT_HOME_TEMPLATE)},[published?.template]);
 function toggleMotion(){
  const next=!motion;setMotion(next);
  try{localStorage.setItem(motionKey,next?'on':'off')}catch{/* The current page still honors the selection when storage is unavailable. */}
 }
 const template=HOME_TEMPLATES.find(item=>item.id===templateId)??HOME_TEMPLATES[0]!,Component=components[template.id]!;
 const activeMotion=motion&&!reduced;
 const theme={'--ht-accent':template.accent,'--ht-paper':template.paper,'--ht-soft':template.soft,'--ht-ink':template.ink} as CSSProperties;
 return <main className={cover?styles['site-home-cover']:styles.homePage} style={theme} data-background-motion={activeMotion?'on':'off'} data-home-template={template.id} data-home-dark={template.homeDark}>
  {preview&&!cover&&<aside className={styles.previewBar} aria-label={t('模板预览状态')}><div><strong>{template.number} · {t(template.name)}</strong><span>{t('原型演示 · 完整预览')}</span></div><a href="/agent/site?tab=home">{t('返回模板设置')}</a></aside>}
  <div className={styles['ht-motion-root']} data-home-motion={activeMotion?'running':'paused'}>
   <MotionContext.Provider value={activeMotion}>
    <MotionControlContext.Provider value={cover?null:{active:activeMotion,disabled:reduced,toggle:toggleMotion}}>
     <Suspense fallback={<div className={styles.templateLoading} role="status">{t('正在准备首页预览…')}</div>}>
      <Component brandName={preview?t('创作空间'):published?.name??'智作云'} subtitle={published?.subtitle||undefined}/>
     </Suspense>
    </MotionControlContext.Provider>
   </MotionContext.Provider>
  </div>
  {!preview&&published&&<PublishedSiteInfo content={published}/>}
  {!preview&&notices.length>0&&<section className="page" aria-label="公开公告"><h2>公告</h2>{notices.map(n=><article key={n.id}><h3>{n.title}</h3><p style={{whiteSpace:'pre-wrap'}}>{n.body}</p></article>)}</section>}
  {!cover&&<div className={styles.templateTools}>
   <span className="sr-only" role="status">{t(reduced?'已遵循系统减少动态效果设置':activeMotion?'背景动态已开启':'背景动态已暂停')}</span>
   <button type="button" aria-label={t(activeMotion?'暂停动态效果':'开启动效')} aria-pressed={activeMotion} disabled={reduced} title={t(reduced?'已遵循系统减少动态效果设置':activeMotion?'暂停动态效果':'开启动效')} onClick={toggleMotion}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{activeMotion?<path d="M9 5v14M15 5v14"/>:<path d="m8 5 11 7-11 7V5Z"/>}</svg>
   </button>
  </div>}
 </main>;
}
